import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import "./GroupDetails.css";
import { io } from "socket.io-client";

const socket = io("https://rhrmapi.up2dateinc.com");

export default function GroupDetails() {
  const navigate = useNavigate();
  const { groupName } = useParams();
  const decodedName = decodeURIComponent(groupName);
  const user = localStorage.getItem("email");

  const [group, setGroup] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  /* ================= LOAD GROUP ================= */
  const fetchGroup = async () => {
    try {
      const res = await axios.get("https://rhrmapi.up2dateinc.com/api/chat/groups");
      const groupList = res.data.groups || [];

      const found = groupList.find(
        g => g.name.trim().toLowerCase() === decodedName.trim().toLowerCase()
      );

      if (!found) {
        alert("Group not found");
        navigate("/chat");
        return;
      }

      if (!found.members.includes(user)) {
        alert("🚫 You are no longer part of this group");
        navigate("/chat");
        return;
      }

      setGroup(found);
    } catch (err) {
      console.error("Fetch group error:", err);
    }
  };

  useEffect(() => {
    fetchGroup();

    socket.emit("join_group", decodedName);

    return () => {
      socket.emit("leave_group", decodedName);
    };
  }, [groupName]);

  /* ================= EXTRA PROTECTION ================= */
  useEffect(() => {
    if (group && !group.members.includes(user)) {
      alert("🚫 Access denied");
      navigate("/chat");
    }
  }, [group, user, navigate]);

  /* ================= LOAD USERS ================= */
  useEffect(() => {
    axios.get(`https://rhrmapi.up2dateinc.com/api/auth/approved/${user}`)
      .then(res => setAllUsers(res.data))
      .catch(err => console.error(err));
  }, []);

  /* ================= SOCKET LISTEN ================= */
  useEffect(() => {

    socket.on("removed_from_group", ({ groupName: gName, removedUser }) => {
      if (gName === groupName && removedUser === user) {
        alert("🚫 You are removed from this group");
        navigate("/chat");
      }
    });

    socket.on("group_updated", ({ groupName: gName, members }) => {
      if (gName === decodedName) {

        if (!members.includes(user)) {
          alert("🚫 You are removed from this group");
          navigate("/chat");
          return;
        }

        setGroup(prev => ({ ...prev, members }));
      }
    });

    return () => {
      socket.off("removed_from_group");
      socket.off("group_updated");
    };
  }, [groupName, navigate, user]);

  /* ================= GET USER NAME ================= */
  const getUserName = (email) => {
    const u = allUsers.find(x => x.email === email);
    return u ? u.name : email;
  };

  /* ================= REMOVE MEMBER ================= */
  const removeMember = async (email) => {
  if (!window.confirm("Remove this member?")) return;

  const updated = {
    ...group,
    members: group.members.filter(m => m !== email)
  };

  // ✅ STEP 1: instant UI update
  setGroup(updated);

  try {
    // ✅ STEP 2: backend call
    await axios.put("https://rhrmapi.up2dateinc.com/api/chat/groups", updated);

    // ✅ STEP 3: socket update
    socket.emit("group_updated", {
      groupName: group.name,
      members: updated.members
    });

    socket.emit("removed_from_group", {
      groupName: group.name,
      removedUser: email
    });

  } catch (err) {
    console.error("Remove error:", err);

    // ❗ rollback if API fails
    fetchGroup();
  }
};

  /* ================= EXIT GROUP ================= */
  const exitGroup = async () => {
  if (!window.confirm("Are you sure you want to exit this group?")) return;

  const updated = {
    ...group,
    members: group.members.filter(m => m !== user)
  };

  // ✅ instant UI
  setGroup(updated);

  try {
    await axios.put("https://rhrmapi.up2dateinc.com/api/chat/groups", updated);

    socket.emit("leave_group", group.name);

    socket.emit("group_updated", {
      groupName: group.name,
      members: updated.members
    });

    navigate("/chat");

  } catch (err) {
    console.error("Exit error:", err);
    fetchGroup();
  }
};

  /* ================= SELECT USERS ================= */
  const toggleUser = (email) => {
    setSelectedUsers(prev =>
      prev.includes(email)
        ? prev.filter(u => u !== email)
        : [...prev, email]
    );
  };

  /* ================= ADD PARTICIPANTS ================= */
  const addParticipants = async () => {
  if (selectedUsers.length === 0) return;

  const updated = {
    ...group,
    members: [...new Set([...group.members, ...selectedUsers])]
  };

  // ✅ STEP 1: instant UI update
  setGroup(updated);
  setSelectedUsers([]);
  setShowAddModal(false);

  try {
    // ✅ STEP 2: backend call
    await axios.put("https://rhrmapi.up2dateinc.com/api/chat/groups", updated);

    // ✅ STEP 3: socket update
    socket.emit("group_updated", {
      groupName: group.name,
      members: updated.members
    });

  } catch (err) {
    console.error("Add error:", err);

    // ❗ rollback if API fails
    fetchGroup();
  }
};
  /* ================= LOADING ================= */
  if (!group) return <h3 style={{ padding: "20px" }}>Loading...</h3>;

  /* ================= UI ================= */
  return (
    <div className="group-page">
      <div className="group-card">

        {/* HEADER */}
        <div className="group-header">

          {/* 🔙 BACK BUTTON */}
          <button className="back-btn" onClick={() => navigate("/chat")}>
            ←
          </button>

          <div className="group-avatar">
            {group.name.charAt(0).toUpperCase()}
          </div>

          <h2>{group.name}</h2>
          <p>{group.members.length} participants</p>
        </div>

        {/* MEMBERS */}
        <div className="group-members">
          {group.members.map((m, i) => (
            <div key={i} className="member-item">

              <div className="member-left">
                <div className="avatar">
                  {getUserName(m).charAt(0).toUpperCase()}
                </div>

                <div className="name">
                  {m === user
                    ? `${getUserName(m)} (You)`
                    : getUserName(m)}
                </div>
              </div>

              {m !== user && (
                <button
                  className="remove-btn"
                  onClick={() => removeMember(m)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {/* ADD */}
        <button className="add-btn" onClick={() => setShowAddModal(true)}>
          ➕ Add Participants
        </button>

        {/* EXIT */}
        <button className="exit-btn" onClick={exitGroup}>
          Exit Group
        </button>

        {/* MODAL */}
        {showAddModal && (
          <div className="modal">
            <div className="modal-box">
              <h3>Select Users</h3>

              {allUsers
                .filter(u => !group.members.includes(u.email))
                .map((u, i) => (
                  <div key={i} className="select-user">
                      <input
                        type="checkbox"
                        className="user-checkbox"
                        checked={selectedUsers.includes(u.email)}
                        onChange={() => toggleUser(u.email)}
                      />
                      <span className="user-name">{u.name}</span>
                    
                  </div>
                ))}

              <div className="modal-actions">
                <button className="confirm-btn" onClick={addParticipants}>
                  Add
                </button>

                <button onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}